import axios, * as Axios from "axios";
import { ApiResult, FieldType, ViewModelBase } from "@shoothill/core";
import { RDDV2Model, RDDV2ModelDTO } from "./RDDV2Model";
import { observable, action, computed, runInAction } from "mobx";
import type { IObservableArray } from "mobx";
import { AppUrls } from "AppUrls";
import { ServerViewModel } from "Globals/ViewModels/ServerViewModel";
import { RDDV2ItemViewModel } from "./RDDV2ItemViewModel";
import { RDDV2ItemResponseViewModel } from "./RDDV2ItemResponseViewModel";
import { RDDV2FilterViewModel } from "./RDDV2FilterViewModel";
import debounce from "lodash/debounce";
import { RDDV2ItemModelDTO } from "./RDDV2ItemModel";
import { IssueViewModel } from "./Issue";
import { StoresInstance } from "Globals/Stores";

export class RDDV2ViewModel extends ViewModelBase<RDDV2Model> {
    private static _instance: RDDV2ViewModel;

    public constructor() {
        super(new RDDV2Model(), false);
        this.setDecorators(RDDV2Model);
    }

    @observable
    public searchString: string = "";

    @observable
    public hasLoaded: boolean = false;

    @observable public errorMessage: string = "";

    @observable
    public projectId: string | null = null;

    @observable
    public rddV2Id: string | null = null;

    @observable
    public selectedSupplierReference: string | null = null;

    @observable
    public rddV2ResponseId: string | null = null;

    @observable
    public rddV2ItemViewModels: IObservableArray<RDDV2ItemViewModel> = observable([]);

    @observable
    public rddV2ItemResponseViewModels: IObservableArray<RDDV2ItemResponseViewModel> = observable([]);

    @computed
    public get getDraftCount(): number {
        const listOfDrafts = this.rddV2ItemViewModels.filter((s) => s.statusId === this.fullStatuses.find((s) => s.type === 1)?.id);
        if (listOfDrafts) {
            return listOfDrafts.length;
        }
        return 0;
    }

    @computed
    public get getClosedCount(): number {
        const listOfClosed = this.rddV2ItemViewModels.filter((s) => s.statusId === this.fullStatuses.find((s) => s.type === 2)?.id);
        if (listOfClosed) {
            return listOfClosed.length;
        }
        return 0;
    }

    @computed
    public get getAwaitingIssueCount(): number {
        const listOfAwaitingIssue = this.rddV2ItemViewModels.filter((s) => s.statusId === this.fullStatuses.find((s) => s.type === 3)?.id);
        if (listOfAwaitingIssue) {
            return listOfAwaitingIssue.length;
        }
        return 0;
    }

    @computed
    public get getRDDV2Items(): RDDV2ItemViewModel[] {
        return this.rddV2ItemViewModels;
    }

    @computed
    public get getRDDV2ItemResponsesLength(): number {
        return this.rddV2ItemResponseViewModels.length;
    }

    @computed
    public get getRDDV2ItemResponses(): RDDV2ItemResponseViewModel[] {
        return this.rddV2ItemResponseViewModels;
    }

    @observable
    public categories = observable<RDDV2CategoryDTO>([]);

    @observable
    public statuses = observable<RDDV2StatusDTO>([]);

    @observable
    public fullStatuses = observable<RDDV2FullStatusDTO>([]);

    @observable
    public addResponse: boolean = false;

    @observable
    public showFiles: boolean = false;

    @observable
    public showStatusChange: boolean = false;

    @action
    public setHasLoaded = (val: boolean) => {
        this.hasLoaded = val;
    };

    @action
    public clean = () => {
        this.rddV2ItemViewModels.replace([]);
    };

    @action
    public setBackGroundStatus(id: string | null): string {
        const status = this.fullStatuses.find((i: any) => i.id === id);
        const color = status?.color;
        return color ? color : "";
    }

    @action
    public setColorStatus(id: string | null): string {
        const status = this.fullStatuses.find((i: any) => i.id === id);
        const color = status?.textColor;
        return color ? color : "";
    }

    @action
    public handleAddResponseShow = (itemId: string | null, rddV2Id: string, projectId: string) => {
        this.rddV2ResponseId = itemId;
        this.rddV2Id = rddV2Id;
        this.projectId = projectId;
        this.addResponse = true;
    };

    @action
    public handleFilesShow = (itemId: string | null, rddV2Id: string, projectId: string) => {
        this.rddV2ResponseId = itemId;
        this.rddV2Id = rddV2Id;
        this.projectId = projectId;
        this.showFiles = true;
    };

    @action
    public handleStatusChangeShow = (itemId: string | null, rddV2Id: string, projectId: string) => {
        this.rddV2ResponseId = itemId;
        this.rddV2Id = rddV2Id;
        this.projectId = projectId;
        this.showStatusChange = true;
    };

    @action
    public handleAddResponseClose = (projectId: string) => {
        this.addResponse = false;
        this.loadRDDV2s(projectId);
    };

    @action
    public handleShowFilesClose = () => {
        this.showFiles = false;
    };

    @action
    public handleStatusChangeClose = () => {
        this.showStatusChange = false;
    };

    @action
    public handleReturnStatus = (rowData: RDDV2ItemViewModel) => {
        return this.statuses.find((s) => s.id === rowData.statusId);
    };

    @action
    public handleReturnResponses = (rowData: RDDV2ItemViewModel) => {
        return this.rddV2ItemResponseViewModels.find((s) => s.rddV2Id === rowData.id);
    };

    @action
    public handleReturnResponseCount = (rowData: RDDV2ItemViewModel) => {
        const responses = this.rddV2ItemResponseViewModels.filter((s) => s.rddV2Id === rowData.id);
        if (responses) {
            return responses.length;
        }
        return 0;
    };

    @action
    public handleStatusId = async (
        e: React.MouseEvent<HTMLSelectElement, MouseEvent>,
        rowData: RDDV2ItemViewModel,
        item: { id: string; displayName: string },
        projectId: string,
    ) => {
        e.stopPropagation();
        e.preventDefault();
        if (StoresInstance.Domain.AccountStore.getCanEditRDDRFI) {
            await this.updateStatus(rowData.id, item.id, projectId);
        }
    };

    @observable
    public issueViewModel: IssueViewModel | null = null;

    @computed
    public get canIssue() {
        return this.rddV2ItemViewModels.some((op) => op.issue);
    }

    @computed
    public get canDisplayIssue() {
        return this.issueViewModel !== null;
    }

    @action
    public displayIssue = (projectId: string | null) => {
        this.projectId = projectId;
        const rddv2Ids = this.rddV2ItemViewModels.filter((op) => op.issue).map((op) => op.id);

        this.issueViewModel = new IssueViewModel(rddv2Ids, this.projectId, this.closeIssue);
    };

    @action
    public closeIssue = (refreshPage: boolean) => {
        this.issueViewModel = null;

        if (refreshPage) {
        }
    };

    @action
    public createViewModels = () => {
        for (const item of this.model.rddV2Items) {
            this.rddV2ItemViewModels.push(new RDDV2ItemViewModel(item));
        }
        for (const itemResponse of this.model.rddV2ItemResponses) {
            this.rddV2ItemResponseViewModels.push(new RDDV2ItemResponseViewModel(itemResponse));
        }
    };

    public getSearchString = () => {
        return computed(() => this.searchString);
    };

    public server: ServerViewModel = new ServerViewModel();

    public loadRDDV2s = async (projectId: string): Promise<ApiResult<RDDV2ModelDTO>> => {
        this.setIsLoading(true);
        let paramsViewModel = RDDV2FilterViewModel.Instance;
        let params = paramsViewModel.model.toDto();
        params.projectId = projectId;
        params.rddV2Id = null;
        params.rddV2StatusId = null;
        params.searchText = this.searchString;

        const apiResult = await this.Post<RDDV2ModelDTO>(AppUrls.Server.Projects.ProjectTrackers.RDDV2.GetRDDV2List, params);
        if (apiResult) {
            if (apiResult.wasSuccessful) {
                runInAction(() => {
                    this.model.rddV2Items = [];
                    this.rddV2ItemViewModels.clear();
                    this.rddV2ItemViewModels = observable([]);
                    this.model.rddV2ItemResponses = [];
                    this.rddV2ItemResponseViewModels.clear();
                    this.categories.replace(apiResult.payload.rddV2Categories);
                    this.statuses.replace(apiResult.payload.rddV2Statuses);
                    this.fullStatuses.replace(apiResult.payload.fullStatuses);
                    this.model.fromDto(apiResult.payload);
                    this.createViewModels();
                });
            } else {
                console.log(apiResult.errors);
            }
        }
        this.setHasLoaded(true);
        this.setIsLoading(false);
        return apiResult;
    };

    public updateStatus = async (id: string, statusId: string, projectId: string): Promise<ApiResult<RDDV2ModelDTO>> => {
        this.setIsLoading(true);
        let paramsViewModel = RDDV2FilterViewModel.Instance;
        let params = paramsViewModel.model.toDto();
        params.projectId = projectId;
        params.rddV2Id = id;
        params.rddV2StatusId = statusId;

        this.projectId = projectId;
        this.rddV2Id = id;

        const apiResult = await this.Post<RDDV2ModelDTO>(AppUrls.Server.Projects.ProjectTrackers.RDDV2.UpdateStatus, params);
        if (apiResult) {
            if (apiResult.wasSuccessful) {
                runInAction(() => {
                    this.model.rddV2Items = [];
                    this.rddV2ItemViewModels.clear();
                    this.rddV2ItemViewModels = observable([]);
                    this.model.rddV2ItemResponses = [];
                    this.rddV2ItemResponseViewModels.clear();
                    this.statuses.replace(apiResult.payload.rddV2Statuses);
                    this.fullStatuses.replace(apiResult.payload.fullStatuses);
                    this.model.fromDto(apiResult.payload);
                    this.createViewModels();

                    const selectedStatus: RDDV2StatusDTO | undefined = this.statuses.find((t) => t.id === statusId);
                    const updatedRDDV2: RDDV2ItemModelDTO | undefined = apiResult.payload.rddV2ListItems.find((t) => t.id === id);
                    if (selectedStatus) {
                        if (updatedRDDV2) {
                            this.selectedSupplierReference = updatedRDDV2.supplierReference;
                        }
                    }
                });
            } else {
                console.log(apiResult.errors);
            }
        }
        this.setHasLoaded(true);
        this.setIsLoading(false);
        return apiResult;
    };

    @action
    public handleSearchChange = (projectId: string, val: string) => {
        console.log(val);
        this.searchString = val;
        RDDV2FilterViewModel.Instance.setValue("searchText", val);

        if (val.length > 3 || val.length === 0) {
            this.reloadDataDelayed(projectId);
        }
    };

    private reloadDataDelayed = debounce((projectId: string) => {
        this.loadRDDV2s(projectId);
    }, 300);

    @action
    public handleRowClick = (e: any, rowData: RDDV2ItemViewModel, projectId: string) => {
        if (StoresInstance.Domain.AccountStore.getCanEditRDDRFI) {
            this.history.push(AppUrls.Client.Project.RDDV2.View.replace(":projectid", projectId).replace(":rddV2id", rowData.id));
        }
    };

    @action
    public handleCreateNew = (projectId: string) => {
        this.history.push(AppUrls.Client.Project.RDDV2.Add.replace(":projectid", projectId));
    };

    @action
    public exportAsCSV = async (projectId: string) => {
        this.setIsLoading(true);

        const requestBody = {
            projectId,
            rddV2Id: null,
            rddV2StatusId: null,
            searchText: "",
        };

        let config: Axios.AxiosRequestConfig = {
            responseType: "blob",
            headers: {
                "Content-Type": "application/json",
            },
        };

        const response = await axios
            .post(AppUrls.Server.Projects.ProjectTrackers.RDDV2.ExportRDDV2List, requestBody, await this.getConfig(true, config))
            .then((response: any) => {
                if (response.status === 200) {
                    const headeRDDV2leName: string = response.headers["content-disposition"].split("filename=")[1].split(";")[0];
                    let fileName = "RDDCSV.csv";
                    if (headeRDDV2leName.endsWith(".csv")) {
                        fileName = headeRDDV2leName;
                    }
                    const url_1 = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement("a");
                    link.href = url_1;
                    link.setAttribute("download", fileName);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url_1);
                }
            })
            .finally(() => this.setIsLoading(false));
    };

    @action
    public handleRevisionClick = (e: any, rowData: RDDV2ItemViewModel, projectId: string) => {
        e.stopPropagation;
        e.preventDefault;
        this.history.push(AppUrls.Client.Project.RDDV2.AddWithRevision.replace(":projectid", projectId).replace(":rddV2id", rowData.id).replace(":isrevision", "1"));
    };

    public async isFieldValid(fieldName: keyof FieldType<RDDV2Model>, value: any): Promise<boolean> {
        let { isValid, errorMessage } = await this.validateDecorators(fieldName);

        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);

        return isValid;
    }

    public afterUpdate: undefined;
    public beforeUpdate: undefined;
}

export interface RDDV2UpdateStatusDTO {
    id: string;
    RDDV2StatusId: string;
}

export interface RDDV2CategoryDTO {
    id: string;
    displayName: string;
}

export interface RDDV2StatusDTO {
    id: string;
    displayName: string;
}

export interface RDDV2FullStatusDTO {
    id: string;
    displayName: string;
    color: string;
    textColor: string;
    type: number;
}
